import { Box, Button, Card, Grid, Typography } from "@mui/material";
import {
  hideLoaderForBatchApiCall,
  showLoaderForBatchApiCall,
} from "utils/helper";
import { IStudentDetailsData } from "utils/interfaces/student";
import { editPurpleIcon, editGreyIcon } from "assets/images";
import React, { useCallback, useEffect, useState } from "react";
import {
  StudnetDetailsPayload,
  StudnetDetailsSuccessPayload,
} from "store/student/types";
import moment from "moment";
import {
  DATE_OF_BIRTH_FORMAT,
  DATE_VIEW_FORMAT,
} from "utils/constants/constant";
import { useHistory, useLocation } from "react-router";
import SLPParticipationModal from "components/SLPParticipationModal";
import { IUpdateSLPParticipationValue } from "utils/interfaces/slp";
import { SLPParticipation } from "utils/enums/slp";
import { UpdateSLPParticipationPayload } from "store/slpDashboard/type";
import {
  getSLPParticipationClass,
  getSLPParticipationText,
} from "utils/helper/slp";
import { toast } from "react-toastify";
import { SLP_PARTICIPATION_UPDATE_MSG } from "utils/constants/Messages";

interface IProfileLabels {
  label: string;
  value: string | number | undefined;
}

const ProfileLabels = ({ details }: { details: IProfileLabels[] }) => (
  <>
    {details.map((row) => (
      <Grid item xs={12} sm={6} md={4} xl={3}>
        <Typography variant="body1" className="sco-detail-title">
          {row.label}
        </Typography>
        <Typography variant="body1" className="sco-detail">
          {row.value}
        </Typography>
      </Grid>
    ))}
  </>
);

interface IStudentDetailsProps {
  studentDetailsRequest: (payload: StudnetDetailsPayload) => {};
  updateSLPParticipationRequest: (payload: UpdateSLPParticipationPayload) => {};
}

const StudentDetails: React.FC<IStudentDetailsProps> = ({
  studentDetailsRequest,
  updateSLPParticipationRequest,
}) => {
  const [studentData, setStudentData] = useState<IStudentDetailsData>();
  const [slpParticipationModalDetail, setSLPParticipationModalDetail] =
    useState<IUpdateSLPParticipationValue>();

  const location = useLocation<{ studentId: number }>();
  const { studentId } = location.state;
  const history = useHistory();

  const onStudentDataSuccess = (response: StudnetDetailsSuccessPayload) => {
    setStudentData(response.studentDetails);
    hideLoaderForBatchApiCall();
  };

  const getStudentData = useCallback(() => {
    if (studentDetailsRequest && studentId) {
      showLoaderForBatchApiCall();
      const payload = {
        values: { id: studentId },
        callback: onStudentDataSuccess,
      };
      studentDetailsRequest(payload);
    } else {
      history.goBack();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentDetailsRequest, location?.state]);

  useEffect(() => {
    getStudentData();
  }, [getStudentData]);

  const handleParticipationModalClose = () => {
    setSLPParticipationModalDetail(undefined);
  };

  const updateParticipationCallback = () => {
    toast.success(SLP_PARTICIPATION_UPDATE_MSG);
    getStudentData();
    handleParticipationModalClose();
  };

  const handleParticipationClick = () => {
    setSLPParticipationModalDetail({
      studentId,
      slpParticipation:
        studentData?.slpParticipation ?? SLPParticipation.Active,
    });
  };

  return (
    <Card className="info-card">
      <Box className="card-inner-head mb-24">
        <Typography variant="h3" sx={{ fontWeight: 500 }}>
          Student Details
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            columnGap: "10px",
          }}
        >
          <Typography variant="body1" sx={{ color: "#9E9E9E" }}>
            SLP Participation
          </Typography>
          <Button
            className={`btn-progress ${getSLPParticipationClass(
              studentData?.slpParticipation
            )}`}
            variant="outlined"
            onClick={handleParticipationClick}
          >
            <img
              src={
                studentData?.slpParticipation === SLPParticipation.Active
                  ? editPurpleIcon
                  : editGreyIcon
              }
              alt="Edit Participation"
              className="default"
            />
            <Typography variant="body2">
              {getSLPParticipationText(studentData?.slpParticipation)}
            </Typography>
          </Button>
        </Box>
      </Box>
      <Grid container spacing={{ xs: "16px", lg: "20px", xl: "24px" }}>
        <ProfileLabels
          details={[
            {
              label: "Full Name",
              value: `${studentData?.firstName} ${studentData?.lastName}`,
            },
            { label: "Email", value: studentData?.emailAddress },
            { label: "Phone", value: studentData?.phoneNumber },
            {
              label: "Date Of Birth",
              value: moment(studentData?.dateOfBirth).format(
                DATE_OF_BIRTH_FORMAT
              ),
            },
            { label: "Street Address", value: studentData?.streetAddress },
            { label: "Suburb", value: studentData?.suburb },
            { label: "State", value: studentData?.state },
            { label: "Post Code", value: studentData?.postCode },
            {
              label: "Contact Method",
              value:
                studentData?.studentEnrolmentDTO
                  ?.enrollmentConfirmationPreference,
            },
            {
              label: "Emergency Contact Name",
              value: studentData?.studentEnrolmentDTO?.emergencyContactName,
            },
            {
              label: "Emergency Contact Relationship",
              value:
                studentData?.studentEnrolmentDTO?.emergencyContactRelationship,
            },
            {
              label: "Emergency Contact Phone",
              value: studentData?.studentEnrolmentDTO?.emergencyContactNumber,
            },
            {
              label: "Last SLP Completed",
              value: studentData?.studentSLPDetails?.lastSLPCompleted
                ? moment(studentData?.studentSLPDetails?.lastSLPCompleted)
                    .format(DATE_VIEW_FORMAT)
                    .toString()
                : "-",
            },
            {
              label: "Next SLP Due Date",
              value: studentData?.studentSLPDetails?.nextSLPDueDate
                ? moment(studentData?.studentSLPDetails?.nextSLPDueDate)
                    .format(DATE_VIEW_FORMAT)
                    .toString()
                : "-",
            },
            {
              label: "Next SLP Number",
              value: studentData?.studentSLPDetails?.nextSLPNumber,
            },
            {
              label: "Number Completed SLPs",
              value: studentData?.studentSLPDetails?.noOfCompletedSLP,
            },
          ]}
        />

        <Grid item xs={12}>
          <Typography variant="body1" className="sco-detail-title">
            Supports Required/Specific Learning Needs
          </Typography>
          <Typography
            variant="body1"
            className="sco-detail"
            sx={{ wordWrap: "break-word" }}
          >
            {studentData?.studentEnrolmentDTO?.information || "-"}
          </Typography>
        </Grid>
      </Grid>
      {!!slpParticipationModalDetail && (
        <SLPParticipationModal
          onClose={handleParticipationModalClose}
          slpParticipationDetail={slpParticipationModalDetail}
          updateSLPParticipationRequest={updateSLPParticipationRequest}
          onSaveCallback={updateParticipationCallback}
        />
      )}
    </Card>
  );
};

export default StudentDetails;
